/**
 * The shell for everything that happens *inside* a client.
 *
 * It resolves the id in the URL exactly once per navigation and decides the
 * three answers a child screen should never have to: still loading, no such
 * client, or here it is. Without this, `/clients/<a-typo>/returns` renders an
 * empty return list — which reads as "this client has filed nothing" rather
 * than "there is no such client". Those are very different sentences to put in
 * front of a practitioner.
 *
 * Cross-tenant ids answer NOT_FOUND, never FORBIDDEN (constitution §3), so this
 * screen is also what a user sees when they paste another firm's link.
 *
 * It also carries the **client context strip**. The platform bar's org control
 * names the firm (ADR-0002: the firm is the org); the taxpayer whose books are
 * open is the second tenancy layer, and it belongs to the content, not the
 * chrome (constitution §7). "Right numbers, wrong client" is this product's
 * most expensive mistake, so the strip is on every client-scoped screen and
 * names the client in full, GSTIN included.
 */

import { Alert, buttonClass } from '@fiveox/ui'
import { ArrowLeft, Building2 } from 'lucide-react'
import { Link, Outlet, useOutletContext } from 'react-router'

import type { ClientRecord } from '../lib/api'
import { clientPath, useScopedClient } from '../lib/clientScope'

export function ClientLayout() {
  const { client, loading, missing, error } = useScopedClient()

  if (loading) return <p className="gst-sub">Loading…</p>

  if (missing || !client) {
    return (
      <div>
        <Link
          to="/clients"
          className={buttonClass({ variant: 'outline', size: 'sm', className: 'mb-4' })}
        >
          <ArrowLeft size={15} /> All clients
        </Link>
        {missing ? (
          <div className="gst-empty">
            <p className="mb-2 font-medium">No such client</p>
            <p>It may have been removed, or it belongs to another firm.</p>
          </div>
        ) : (
          <Alert tone="error">{error ?? 'That client could not be loaded.'}</Alert>
        )}
      </div>
    )
  }

  return (
    <>
      <div className="mb-6 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-border bg-surface-raised px-4 py-2.5 shadow-sm">
        <Link
          to="/clients"
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> All clients
        </Link>
        <span aria-hidden className="text-border">
          /
        </span>
        <Link
          to={clientPath(client.id)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary"
        >
          <Building2 size={15} className="text-primary" /> {client.legal_name}
        </Link>
        <span className="gst-mono">{client.gstin}</span>
        {client.status === 'archived' && <span className="gst-chip">Archived</span>}
        <Link
          to="/clients"
          className={buttonClass({ variant: 'ghost', size: 'sm', className: 'ml-auto' })}
        >
          Switch client
        </Link>
      </div>
      <Outlet context={{ client } satisfies ClientContext} />
    </>
  )
}

export interface ClientContext {
  client: ClientRecord
}

/** For screens rendered inside `ClientLayout`. The client is always present. */
export function useClient(): ClientRecord {
  return useOutletContext<ClientContext>().client
}
